import React, { useState, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import { z } from 'zod';
import { resourceTypes, fetchAliasesByResourceType } from './aliasService';
import { AliasEntry } from './types';

// Form schema for selecting resource type and filtering aliases
const SearchSchema = z.object({
  resourceType: z.string().nonempty('Resource type is required'),
  aliasFilter: z.string().optional(),
});
type SearchValues = z.infer<typeof SearchSchema>;

/**
 * AliasSearch component for discovering and searching Azure Policy aliases
 * 
 * This component provides an enhanced interface for:
 * - Searching resource types with autocomplete
 * - Filtering aliases by name with real-time results
 * - Displaying modifiable status with visual indicators
 * - Copy-to-clipboard functionality for easy use
 * 
 * @example
 * <AliasSearch />
 */
const AliasSearch: React.FC = () => {
  const [results, setResults] = useState<AliasEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [showModifiableOnly, setShowModifiableOnly] = useState(false);
  const debounceRef = useRef<number | undefined>(undefined);
  const aliasDebounceRef = useRef<number | undefined>(undefined);
  const [filteredResults, setFilteredResults] = useState<AliasEntry[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  
  // New state for dropdown functionality
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [filteredTypes, setFilteredTypes] = useState<string[]>([]);
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  const typeInputRef = useRef<HTMLInputElement>(null);

  const formik = useFormik<SearchValues>({
    initialValues: { resourceType: '', aliasFilter: '' },
    onSubmit: () => {},
  });

  // Filter resource types based on search term
  useEffect(() => {
    if (formik.values.resourceType) {
      const filtered = resourceTypes.filter(type =>
        type.toLowerCase().includes(formik.values.resourceType.toLowerCase())
      );
      setFilteredTypes(filtered.slice(0, 200)); // Show up to 200 matching results
    } else {
      setFilteredTypes(resourceTypes.slice(0, 200)); // Show first 200 by default
    }
  }, [formik.values.resourceType]);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
        setShowTypeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Copy alias to clipboard
  const copyToClipboard = async (aliasId: string) => {
    try {
      // Extract just the alias name without the properties path
      const aliasName = aliasId.replace(/\s*\(properties\.[^)]*\)$/, '');
      await navigator.clipboard.writeText(aliasName);
      setCopiedId(aliasId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // Effect for resource type search (existing logic)
  useEffect(() => {
    const rt = formik.values.resourceType.trim();
    clearTimeout(debounceRef.current);
    if (!rt) {
      setResults(null);
      setFilteredResults([]);
      return;
    }
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      const data = await fetchAliasesByResourceType(rt);
      setResults(data);
      setLoading(false);
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [formik.values.resourceType]);

  // Effect for alias filtering with debouncing
  useEffect(() => {
    clearTimeout(aliasDebounceRef.current);
    
    if (!results) {
      setFilteredResults([]);
      return;
    }

    aliasDebounceRef.current = window.setTimeout(() => {
      const aliasSearchTerm = formik.values.aliasFilter?.trim().toLowerCase() || '';
      
      let filtered = results;
      
      // Filter by alias search term if provided
      if (aliasSearchTerm) {
        filtered = filtered.filter(alias => 
          alias.id.toLowerCase().includes(aliasSearchTerm)
        );
      }
      
      // Apply modifiable filter
      if (showModifiableOnly) {
        filtered = filtered.filter(alias => alias.modifiable);
      }
      
      setFilteredResults(filtered);
    }, 300);

    return () => clearTimeout(aliasDebounceRef.current);
  }, [results, formik.values.aliasFilter, showModifiableOnly]);

  return (
    <div className="flex flex-col space-y-6 w-full min-w-0 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm">
      <div className="space-y-6 w-full">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Azure Policy Alias Search</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Discover and explore Azure resource aliases for policy conditions</p>
            </div>
          </div>
        </div>

        {/* Search Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
            </svg>
            Search Configuration
          </h3>

          {/* Resource Type Search */}
          <div className="space-y-4">
            <div>
              <label htmlFor="resourceType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Resource Type
              </label>
              <div className="relative" ref={typeDropdownRef}>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  ref={typeInputRef}
                  id="resourceType"
                  name="resourceType"
                  placeholder="e.g., Microsoft.Compute/virtualMachines"
                  onChange={formik.handleChange}
                  onFocus={() => setShowTypeDropdown(true)}
                  value={formik.values.resourceType}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  style={{ minWidth: '0' }}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  {loading ? (
                    <svg className="animate-spin h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg 
                      className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${showTypeDropdown ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>

                {/* Dropdown */}
                {showTypeDropdown && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                    {filteredTypes.length > 0 ? (
                      <>
                        <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400">
                          {filteredTypes.length === resourceTypes.slice(0, 200).length 
                            ? `Showing ${filteredTypes.length} of ${resourceTypes.length} types. Type to search...` 
                            : `Showing ${filteredTypes.length} matching types ${filteredTypes.length >= 200 ? '(limited to 200)' : ''}`
                          }
                        </div>
                        {filteredTypes.map((type) => (
                          <button
                            key={type}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                            onClick={() => {
                              formik.setFieldValue('resourceType', type);
                              setShowTypeDropdown(false);
                              typeInputRef.current?.blur();
                            }}
                          >
                            {type}
                          </button>
                        ))}
                      </>
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                        No resource types found matching "{formik.values.resourceType}"
                      </div>
                    )}
                  </div>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Start typing to see available resource types or explore popular ones
              </p>
            </div>

            {/* Alias Filter Input - only show when we have results */}
            {results && results.length > 0 && (
              <div>
                <label htmlFor="aliasFilter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Filter Aliases
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
                    </svg>
                  </div>
                  <input
                    id="aliasFilter"
                    name="aliasFilter"
                    placeholder="Filter by alias name..."
                    onChange={formik.handleChange}
                    value={formik.values.aliasFilter || ''}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Narrow down results by typing part of the alias name
                  </p>
                  <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                    {filteredResults.length} of {results.length} aliases
                  </span>
                </div>
              </div>
            )}

            {/* Show modifiable-only toggle */}
            {results && results.length > 0 && (
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  <input
                    id="showModifiableOnly"
                    type="checkbox"
                    checked={showModifiableOnly}
                    onChange={e => setShowModifiableOnly(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <label htmlFor="showModifiableOnly" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Show modifiable aliases only
                  </label>
                  <div className="group relative">
                    <svg className="h-4 w-4 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                      Modifiable aliases can be used with "modify" effect
                    </div>
                  </div>
                </div>
                {showModifiableOnly && (
                  <div className="flex items-center text-xs text-green-600 dark:text-green-400">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Showing modifiable only
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Results Section */}
        {results && results.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Search Results
                </h3>
                <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Modifiable: {filteredResults.filter(r => r.modifiable).length}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                    Read-only: {filteredResults.filter(r => !r.modifiable).length}
                  </span>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              {filteredResults.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Alias ID
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32">
                        Status
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredResults.map(r => (
                      <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150">
                        <td className="px-6 py-4">
                          <div className="text-sm font-mono text-gray-900 dark:text-gray-100 break-all">
                            {r.id}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {r.modifiable ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 whitespace-nowrap">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Modifiable
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 whitespace-nowrap">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                              </svg>
                              Read-only
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => copyToClipboard(r.id)}
                            className="inline-flex items-center p-1.5 border border-transparent rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            title="Copy to clipboard"
                          >
                            {copiedId === r.id ? (
                              <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="px-6 py-12 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No aliases found</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    No aliases match your current search criteria. Try adjusting your filters.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty state for no search */}
        {!results && !loading && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
            <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Start Your Search</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              Enter a resource type above to discover available Azure Policy aliases. These aliases are essential for creating effective policy conditions.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl mx-auto text-sm">
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">What are aliases?</div>
                <div className="text-gray-600 dark:text-gray-400">
                  Aliases provide access to specific properties for Azure resource types in policy conditions.
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">Modifiable vs Read-only:</div>
                <div className="text-gray-600 dark:text-gray-400">
                  Modifiable aliases can be used with "modify" effects to change resource properties.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AliasSearch; 